<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount } from "svelte";
    import Banner from "$/components/ui/Banner.svelte";
    import ExtensionNav from "./components/ExtensionNav.svelte";
    import LoginPanel from "./components/LoginPanel.svelte";
    import PageSelect from "./components/pageSelect/PageSelect.svelte";
    import Settings from "./components/Settings.svelte";
    import ShareInput from "./components/ShareInput.svelte";
    import SharePreferencesPanel from "./components/SharePreferencesPanel.svelte";
    import Card from "./components/card/Card.svelte";
    import BottomPopUpModal from "./components/modals/BottomPopUpModal.svelte";
    import { BottomPopUpModalAction } from "./components/modals/BottomPopUpModalAction";
    import { SelectedSnippetsStore } from "./stores/SelectedSnippetsStore";
    import { extensionInit } from "./stores/StoreInitializer";
    import { TokenStore } from "./stores/TokenStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "./stores/extension/ActiveTopicStore";
    import {
        PreferencesStore,
        PreferencesStoreInitialized,
    } from "./stores/global/PreferencesStore";
    import { LocalStorage } from "./services/LocalStorage";
    import TopicEmptyStateImg from "./images/topic-empty-state.png";
    import SnippetEmptyStateImg from "./images/snippet-empty-state.png";
    import { TENANT_STORAGE_KEY } from "$/services/TenantService";
    import ErrorImg from "./images/error/Error-404.png";
    import LoadingGif from "./images/loading-cat-jerry.gif";
    import { ServiceConsts } from "./services/ServiceConsts";
    import { TenantStore } from "./stores/TenantStore";
    import { TextFragmentUtils } from "./utils/TextFragmentUtils";
    import { clickAction } from "./utils/clickAction";
    import NewCard from "./components/card/NewCard.svelte";
    import SaveLinkedinPost from "./components/saveLinkedinPosts/saveLinkedinPost.svelte";
    import SaveQuoraPosts from "./components/saveQuoraPosts/saveQuoraPosts.svelte";
    import SaveRedditPosts from "./components/saveRedditPosts/saveRedditPosts.svelte";
    import SaveXPosts from "./components/saveXPosts/saveXPosts.svelte";
    import SaveYoutube from "./components/saveYoutube/saveYoutube.svelte";
    import { ClusterStore } from "./stores/ClusterStore";
    import Sortable from "sortablejs";
    import BookmarkButton from "./components/BookmarkButton.svelte";
    import PdfPanel from "./components/extension/PdfPanel.svelte";
    import ImportKindle from "./components/extension/ImportKindle.svelte";
    import { ExtensionPanels, ExtensionStore } from "./stores/ExtensionStore";
    import { visualizeSnippets } from "./utils/CustomLocateV4/marker";
    import { createQuery, getContainer } from "./utils/CustomLocateV4/query";
    import HighlightOptions from "./components/highlightOptions/HighlightOptions.svelte";
    import { highlightSnippets, cleanupAllHighlights, cleanupAllTriggers } from "./utils/CustomLocateV4/marker/utils";
    import {
        destroyDragingCardGhostElement,
        generateDraggingCardGhostAndSetCanCopy,
        getRole,
        makeElementImagesDraggable,
        SNIPPETS_WRAPPER_ID,
        getRootDocument,
        hasParentWithClass,
        pushWebsiteContent,
        isPublicPDF,
        clearUrlByMarker,
        PDF_DEFAULT_HIGHLIGHTER_COLOR,
        isPdfViewer,
        generateDocumentObject,
        isSocialMediaLink,
    } from "./utils/Utils";
    import { SelectionUtils } from "./utils/SelectionUtils";
    import { CardTitleUtils } from "./components/card/CardTitle/CardTitleUtils";
    import { AppStore, AppType } from "./stores/AppStore";
    import { fade } from "svelte/transition";
    import { v4 as uuid } from "uuid";
    import { KnowledgeTriggerStore } from "$/stores/KnowledgeTriggerStore";
    import { HighlightStore } from "$/stores/HighlightStore";
    import { get } from "svelte/store";
    import { highlightAndScrollToSnippet, scrollToSnippet } from "./utils/CustomLocateV4/locator";

    let userRole;
    let snippetsWrapperRef;
    let snippetsWrapper;
    let sortableDragAndDrop;
    let showCheckIcon = false;
    let showError = false;
    let selectionHtml = null;
    let dispatch = createEventDispatcher();

    const sortableChosenClass = "sortable-chosen";

    const deleteModalAction = new BottomPopUpModalAction(
        async () => {
            await deleteModalAction.deleteSelectedSnippets(
                $SelectedSnippetsStore,
            );
        },
        async () => {
            SelectedSnippetsStore.clear();
        },
        "Delete",
        "Deleting snippets...",
        "pink",
    );

    const dragAndDropConfig = {
        handle: ".card-header",
        chosenClass: sortableChosenClass,
        swapThreshold: 0.3,
        invertSwap: true,
        animation: 150,
        onEnd(event) {
            // SortableJS is setting images draggable attribute to false so we need to reset it after drop
            makeElementImagesDraggable(event.item);
            ActiveTopicStore.changeSnippetPosition(
                event.oldIndex,
                event.newIndex,
            );
        },
    };

    $: subscriptionExpired =
        $TenantStore && $TokenStore && TenantStore.isExpired();

    $: if ($ActiveTopic)
        userRole = getRole(
            $ActiveTopic,
            $TokenStore.user?.id,
            $TenantStore?.tenants,
            $ClusterStore.clusters,
        );

    $: noLocalSnippetsYet =
        ($ClusterStore === null ||
            ClusterStore.getAllClusterTopics()?.length === 0) &&
        ($ActiveTopic === null || $ActiveTopic?.snippets?.length === 0);

    $: isOwner = $TenantStore && TenantStore.isOwner($TokenStore?.user?.id);

    $: userRole, sortableDragAndDrop && setDragAndDropPermission();

    $: snippetsWrapperRef, snippetsWrapperRef && initSortable();

    $: initialText = `<strong>${$SelectedSnippetsStore.length} ${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong> selected`;

    $: deleteConfirmationText =
        `Delete <strong>${$SelectedSnippetsStore.length}` +
        `${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong>?`;

    $: isUserOnKindle =
        document.location.origin === ServiceConsts.KINDLE_URL &&
        document.location.pathname.includes(
            ServiceConsts.KINDLE_HIGHLIGHTS_PATH,
        );

    $: {
        if ($PreferencesStoreInitialized) {
            //extension pushing website when opened
            if ($PreferencesStore?.betaFeatures && $ExtensionStore.opened) {
                document.documentElement.classList.add(
                    "root-website-animation",
                );
                document.documentElement.classList.add("shrink-root-website");
            } else {
                document.documentElement.classList.remove(
                    "root-website-animation",
                );
                document.documentElement.classList.remove(
                    "shrink-root-website",
                );
            }

            //placement of the extension panel (right or left)
            if ($PreferencesStore?.positionPanelRight) {
                document.documentElement.classList.remove("left-panel");
            } else {
                if ($PreferencesStore?.betaFeatures)
                    document.documentElement.classList.add("left-panel");
                else document.documentElement.classList.remove("left-panel");
            }
        }
    }
    $: $ActiveTopic, resetErrorFlag();
    $: $PreferencesStore, highlightSnippets();

    async function highlightActiveSnippet() {

        let snippetURLId = new URLSearchParams(
            window.location.hash.substring(1),
        ).get(ServiceConsts.SNIPPET_PARAM_NAME);

        let snippet = await ActiveTopicStore.getSnippet(snippetURLId);

        if(!snippet) return;

        // for now if url is linkedin.com/feed/update then we don't scroll to element
        // because we are already on the page of one post or linkedin feed which we cannot highlight
        if (isSocialMediaLink(snippet.url)) {
            window.getSelection().removeAllRanges();
        } else {
            if ($PreferencesStore?.showAllHighlights && ActiveTopicStore.hasSnippet(snippet.id))
                scrollToSnippet(snippet);
            else highlightAndScrollToSnippet(snippet);
        }
        
        AppStore.setFocused({ snippetId: snippet.id });

        clearUrlByMarker("#");
    }

    // Auto scroll to new card on new selection
    $: if (
        !$ExtensionStore.loading &&
        $ExtensionStore.card.content !== "" &&
        userRole >= TopicAccessLevel.EDITOR
    ) {
        // If the user has selected to add cards to the bottom, scroll to the bottom else scroll to the top.
        scrollToNewCard();
    }

    const scrollToNewCard = () => {
        requestAnimationFrame(() => {
            if (snippetsWrapper?.scrollHeight) {
                snippetsWrapper.scrollTop =
                    $PreferencesStore.addCardsToTheBottom
                        ? snippetsWrapper.scrollHeight
                        : 0;
            }
        });
    };

    const resetErrorFlag = () => {
        showError = false;
    };

    const togglePanelVisibility = () => {
        ExtensionStore.setOpened(!$ExtensionStore.opened);

        if ($PreferencesStore?.betaFeatures) {
            pushWebsiteContent(!$ExtensionStore.opened);
        }
    };

    const animateExtensionOpenIcon = () => {
        showCheckIcon = true;
        setTimeout(() => {
            showCheckIcon = false;
        }, 2000);
    };

    const getTopicIdFromUrl = () => {
        let urlParams = new URLSearchParams(window.location.hash.substring(1));
        let topicId = urlParams
            .get(ServiceConsts.TOPIC_PARAM_NAME)
            ?.split("#")[0];

        return topicId === "null" ? null : topicId;
    };

    const setDragAndDropPermission = () => {
        sortableDragAndDrop.option(
            "disabled",
            userRole < TopicAccessLevel.EDITOR,
        );
    };

    const initSortable = () => {
        sortableDragAndDrop = new Sortable(
            snippetsWrapperRef,
            dragAndDropConfig,
        );
    };

    const createSnippetOnEnter = (event) => {
        if (
            event.key === "Enter" &&
            $ExtensionStore.card.content !== "" &&
            userRole >= TopicAccessLevel.EDITOR
        ) {
            createSnippetFromSelectedText($TokenStore?.user);
            if (!$ExtensionStore.opened)
                event.shiftKey
                    ? animateExtensionOpenIcon()
                    : togglePanelVisibility();
        }
    };

    const handleTextSelection = async () => {
        if (!selectionHtml || !$ActiveTopic) return;

        let selection = document.getSelection();

        if (!selection) {
            selectionHtml.innerHTML = "";
            ExtensionStore.clearNewCardContent();
            return;
        }

        // Check if the selection is in an input, textarea, or contenteditable element
        let parentElement = selection.anchorNode?.parentElement;
        while (parentElement) {
            if (
                parentElement.tagName === "INPUT" ||
                parentElement.tagName === "TEXTAREA" ||
                parentElement.isContentEditable
            ) {
                selectionHtml.innerHTML = "";
                ExtensionStore.clearNewCardContent();
                return;
            }
            parentElement = parentElement.parentElement;
        }

        // Ignore selection in extension
        if (selection.anchorNode && hasParentWithClass(selection.anchorNode, "js-collabwriting-app"))
            return;

        selectionHtml.innerHTML = "";

        selectionHtml.innerHTML = SelectionUtils.getCleanContentFromSelection(
            selection,
            document,
            window.location,
        );
        $ExtensionStore.card.content = selectionHtml.innerHTML;

        if (selection.anchorNode && selection.focusNode) {
            $ExtensionStore.card.highlight = {
                ...$ExtensionStore.card.highlight,
                // get anchor and focus nodes queries to be able to restore selection
                anchorNode: createQuery(selection.anchorNode),
                anchorOffset: selection.anchorOffset,
                focusNode: createQuery(selection.focusNode),
                focusOffset: selection.focusOffset,
                container: createQuery(getContainer(selection)),
                postContent: null,
                preContent: null,
            };
        }
    };

    const createSnippetFromSelectedText = async (user) => {
        if ($ExtensionStore?.card?.content === "") return;

        sortableDragAndDrop.option("disabled", true);

        let snippetId = uuid();

        const { preContent, postContent } =
            TextFragmentUtils.generatePreAndPostContent(window.getSelection());
        $ExtensionStore.card.highlight.preContent = preContent;
        $ExtensionStore.card.highlight.postContent = postContent;

        let snippet = {
            id: snippetId,
            content: $ExtensionStore.card.content,
            nameSuggestion: CardTitleUtils.generateTitle(document),
            highlight: $ExtensionStore.card.highlight,
            comments: [],
            url: SelectionUtils.getUrlFromSelection(document, window.location),
            createdBy: user,
            createdOn: new Date().getTime(),
            document: generateDocumentObject(window.location.href),
            color: PDF_DEFAULT_HIGHLIGHTER_COLOR,
            verified: true,
        };

        dispatch("snippetCreated", { snippet });

        if (!isPdfViewer()) {
            visualizeSnippets([snippet]);
        }

        clearUrlByMarker("#");

        selectionHtml.innerHTML = "";

        ExtensionStore.clearNewCardContent();

        ActiveTopicStore.createSnippet(snippet);

        sortableDragAndDrop.option(
            "disabled",
            userRole < TopicAccessLevel.EDITOR,
        );

        window.getSelection().removeAllRanges();

        if ($PreferencesStore.showAllHighlights) highlightSnippets();
    };

    const loadTenantAndClusters = async () => {
        await TenantStore.init();
        await ClusterStore.init($TenantStore.active.id);
    };

    const deleteSnippetAndHighlightSnippets = async (snippetId) => {
        await ActiveTopicStore.deleteSnippet(snippetId);
        dispatch("snippetDeleted", { id: snippetId });
        if ($PreferencesStore.showAllHighlights) highlightSnippets();
    };

    const dragCardEventListener = (event: DragEvent) => {
        generateDraggingCardGhostAndSetCanCopy(
            isPdfViewer() ? AppType.DASHBOARD : AppType.EXTENSION,
            event,
            sortableChosenClass,
        );
    };

    // Initialize extension
    LocalStorage.get("CONTEXT_MENU_OPTIONS").then(async (contextMenuAction) => {
        await ExtensionStore.init();

        // sleep in intervals of 100ms until the $TokenStore is inintialized
        while ($TokenStore.initialized !== true)
            await new Promise((resolve) => {
                setTimeout(resolve, 100);
            });

        if ($TokenStore.expired) return;

        LocalStorage.remove("CONTEXT_MENU_OPTIONS");

        let topicId = getTopicIdFromUrl();

        if (contextMenuAction === "create" && !subscriptionExpired) {
            ExtensionStore.setLoadingStatus(true);
            await loadTenantAndClusters();
            await ExtensionStore.createTopic($TenantStore?.active.id);
            ExtensionStore.setLoadingStatus(false);
            handleTextSelection();
        } else if (topicId !== undefined) {
            ExtensionStore.setLoadingStatus(true);

            try {
                if ($TenantStore === null) await TenantStore.init();
                if ($ClusterStore?.clusters === null)
                    await ClusterStore.init($TenantStore.active.id);

                await ActiveTopicStore.init(
                    topicId,
                    TokenStore,
                    TenantStore,
                    ClusterStore,
                );
            } catch (error) {
                showError = true;
            }

            if ($ActiveTopic !== null) {
                if (
                    $ActiveTopic.tenant?.id !== $TenantStore.active.id &&
                    TenantStore.contains($ActiveTopic.tenant?.id)
                ) {
                    await TenantStore.setActive($ActiveTopic.tenant);
                    await ClusterStore.init($TenantStore.active.id);
                }
            } else {
                await extensionInit();
            }

            ExtensionStore.setLoadingStatus(false);
        } else {
            await extensionInit();
            handleTextSelection();
        }

        requestAnimationFrame(async () => {
            if(!isPdfViewer()) {
                await highlightSnippets();
                await highlightActiveSnippet();
            }
        }) 

        // add context menu listener for when app is already initialized
        // and user uses context menu
        chrome.runtime?.onMessage?.addListener((msg, sender, sendResponse) => {
            if (
                msg.action == "createNewTopicFromContextMenu" &&
                userRole >= TopicAccessLevel.EDITOR
            ) {
                // remove flag used only when initializing extension
                LocalStorage.remove("CONTEXT_MENU_OPTIONS");

                // show panel if hidden
                if (!$ExtensionStore.opened) togglePanelVisibility();

                ExtensionStore.createTopic($TenantStore?.active.id);
            } else if (
                msg.action == "openPanelFromContextMenu" &&
                !$ExtensionStore.opened
            ) {
                togglePanelVisibility();
            }

            return true;
        });
    });

    onMount(async () => {

        if (isPublicPDF()) await TokenStore.init();

        getRootDocument().addEventListener("dragstart", dragCardEventListener);
        getRootDocument().addEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
        getRootDocument("web").addEventListener(
            "selectionchange",
            handleTextSelection,
        );
        getRootDocument("web").addEventListener(
            "keydown",
            createSnippetOnEnter,
        );
    });

    onDestroy(() => {
        getRootDocument().removeEventListener(
            "dragstart",
            dragCardEventListener,
        );
        getRootDocument().removeEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
        getRootDocument("web").removeEventListener(
            "selectionchange",
            handleTextSelection,
        );
        getRootDocument("web").removeEventListener(
            "keydown",
            createSnippetOnEnter,
        );
    });

    function handleHighlightClicked(event: CustomEvent) {
        const { snippetId } = event.detail;
        
        const clickedHighlight = HighlightStore.getHighlight(snippetId);
        let group = clickedHighlight?.group;

        if (group === "cw-trigger") {
            const snippetToSend = {
                content: clickedHighlight.content,
                highlight: clickedHighlight.highlight
                    ? {
                        container: clickedHighlight.highlight.container || "body", 
                        anchorNode: clickedHighlight.highlight.anchorNode || "body", 
                        anchorOffset: clickedHighlight.highlight.anchorOffset || 0,
                        focusNode: clickedHighlight.highlight.focusNode || "body", 
                        focusOffset: clickedHighlight.highlight.focusOffset || 0
                    }
                    : {
                        container: "body", 
                        anchorNode: "body",
                        anchorOffset: 0,
                        focusNode: "body",
                        focusOffset: 0
                    },
                originalId: clickedHighlight.originalId,
                url: clickedHighlight.url
            };
            
            ActiveTopicStore.createSnippet(snippetToSend);
            highlightSnippets();
            
        } else {

            AppStore.setFocused({ snippetId: clickedHighlight.id });
            if (snippetsWrapper) {
                const focusedElement = snippetsWrapper.querySelector(`[data-snippet-id="${clickedHighlight.id}"]`);
                if (focusedElement) {
                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }

</script>

<HighlightOptions on:highlight-clicked={handleHighlightClicked} />

{#if $PreferencesStoreInitialized}
    {#if isPublicPDF() && !$TokenStore.expired}
        <PdfPanel />
    {:else}
        {#if userRole != undefined}
            <SaveLinkedinPost editRole={userRole} />
            <SaveXPosts editRole={userRole} />
            <SaveRedditPosts editRole={userRole} />
            <SaveQuoraPosts editRole={userRole} />
            <SaveYoutube editRole={userRole} />
        {/if}
        <div
            class="arrow js-collabwriting-arrow"
            class:arrow--panel-right={$PreferencesStore.positionPanelRight}
            class:arrow--panel-right--hidden={!$ExtensionStore.opened}
            use:clickAction={togglePanelVisibility}
        >
            {#if showCheckIcon}
                <svg
                    class:rotate={$PreferencesStore.positionPanelRight}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            {:else}
                <svg
                    class:rotate={!$ExtensionStore.opened}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 17l-5-5m0 0l5-5m-5 5h12"
                    />
                </svg>
            {/if}
        </div>
        <div
            class="app-wrapper"
            class:hide={!$ExtensionStore.opened}
            class:app-wrapper--panel-right={$PreferencesStore.positionPanelRight}
        >
            <main
                class="js-collabwriting-app"
                on:keypress|stopPropagation
                on:keyup|stopPropagation
                on:keydown|stopPropagation
            >
                <ExtensionNav />

                {#if $ExtensionStore.activePanel === ExtensionPanels.SETTINGS}
                    <Settings
                        standalone={false}
                        on:close={() =>
                            ExtensionStore.setPanel(ExtensionPanels.MAIN)}
                    />
                {:else if $ExtensionStore.activePanel === ExtensionPanels.SHARE}
                    <SharePreferencesPanel
                        topic={$ActiveTopic}
                        user={$TokenStore?.user}
                        showDashboardButton={true}
                        on:close={() =>
                            ExtensionStore.setPanel(ExtensionPanels.MAIN)}
                    />
                {:else if $ExtensionStore.activePanel === ExtensionPanels.MAIN}
                    {#if !$TokenStore.initialized}
                        {#await TokenStore.init()}
                            <div class="cards">
                                <div class="loading">
                                    <img
                                        class="loading-gif"
                                        src={LoadingGif}
                                        alt="Loading gif"
                                    />
                                    <div class="loading__text">
                                        <span>Authorizing</span>
                                    </div>
                                </div>
                            </div>
                        {/await}
                        <!-- If user is not logged in -->
                    {:else if $TokenStore.expired}
                        <LoginPanel></LoginPanel>

                        <!-- If user is logged in -->
                    {:else}
                        <!-- Used for fetching a HTML selection for future processing -->
                        <div class="--hide" bind:this={selectionHtml}></div>

                        {#if $ClusterStore}
                            <PageSelect />
                        {/if}

                        {#if !$ExtensionStore.loading}
                            <!-- If workspace subscription has expired show Subscription Expired Banner -->
                            {#if subscriptionExpired}
                                <Banner
                                    variant="warning"
                                    message={isOwner
                                        ? "Oops, your team subscription has expired! Renew now for full access!"
                                        : "Oops, your team subscription has expired! Contact the workspace owner to renew it!"}
                                    actionButton={isOwner
                                        ? {
                                              label: "Renew",
                                              onClick: () =>
                                                  window.open(
                                                      `${ServiceConsts.DASHBOARD_URL}/#/${$TenantStore?.active?.id}/payment`,
                                                      "_blank",
                                                  ),
                                          }
                                        : null}
                                />

                                <!-- Else if user doesn't have Editor Acces for that Topic show Permission Missing Banner -->
                            {:else if selectionHtml && selectionHtml.innerHTML !== "" && userRole < TopicAccessLevel.EDITOR}
                                <div in:fade out:fade>
                                    <Banner
                                        variant="warning"
                                        message="You don't have the permission to add new snippets to this topic."
                                        actionButton={null}
                                    />
                                </div>
                            {/if}

                            {#if isUserOnKindle && $ExtensionStore.opened && userRole >= TopicAccessLevel.EDITOR}
                                <ImportKindle />
                            {/if}
                        {/if}

                        <div
                            id={SNIPPETS_WRAPPER_ID}
                            class="cards"
                            bind:this={snippetsWrapper}
                        >
                            {#if $ExtensionStore.loading}
                                <div class="loading">
                                    <img
                                        class="loading-gif"
                                        src={LoadingGif}
                                        alt="Loading gif"
                                    />
                                    <div class="loading__text">
                                        <span>Searching for your content</span>
                                    </div>
                                </div>
                            {:else}
                                <div
                                    class="mt-4 flex flex-col gap-4 pb-6"
                                    bind:this={snippetsWrapperRef}
                                >
                                    {#if $ExtensionStore.card.content && userRole >= TopicAccessLevel.EDITOR}
                                        <NewCard
                                            on:create={() =>
                                                createSnippetFromSelectedText(
                                                    $TokenStore?.user,
                                                )}
                                        />
                                    {/if}

                                    {#if $ActiveTopic?.snippets && $ActiveTopic?.snippets?.length > 0}
                                        {#each $ActiveTopic.snippets as snippet, index (snippet.id)}
                                            <Card
                                                card={snippet}
                                                user={$TokenStore?.user}
                                                editRole={userRole}
                                                openInNewTab={false}
                                                topicId={$ActiveTopic.id}
                                                deleteSnippet={deleteSnippetAndHighlightSnippets}
                                                createComment={ActiveTopicStore.createComment}
                                                updateComment={ActiveTopicStore.updateComment}
                                                deleteComment={ActiveTopicStore.deleteComment}
                                                bgColor="#F3F4F6"
                                                isExtension={true}
                                                hideReactions={ActiveTopicStore.isInbox()}
                                                ordinal={index}
                                            />
                                        {/each}
                                    {/if}
                                </div>

                                {#if showError}
                                    <div class="loading">
                                        <img
                                            class="error-img"
                                            src={ErrorImg}
                                            alt="Error"
                                        />
                                        <div class="loading__text">
                                            <span
                                                >We couldn't load this page 😔</span
                                            >
                                        </div>
                                    </div>
                                {/if}

                                <!-- show tutorial images when there are no snippets, cards or active highlight selections -->
                                {#if $ExtensionStore.card.content === "" && noLocalSnippetsYet}
                                    <div class="empty-state">
                                        <img
                                            src={TopicEmptyStateImg}
                                            alt="Empty list"
                                        />
                                        <div class="empty-state--text_wrapper">
                                            <span
                                                ><strong>Topics</strong> are
                                                where you store different
                                                <strong>Snippets</strong> from different
                                                sources you find online.</span
                                            >
                                            <span
                                                >Click<strong> + </strong>to add
                                                new
                                                <strong>Topic</strong></span
                                            >
                                        </div>
                                    </div>
                                {:else if $ActiveTopic?.snippets?.length === 0 && (userRole === TopicAccessLevel.NO_ACCESS || userRole === TopicAccessLevel.VIEWER)}
                                    <div class="empty-state">
                                        <img
                                            src={SnippetEmptyStateImg}
                                            alt="Empty snippet"
                                            class="empty-snippet__image"
                                        />
                                        <div class="empty-state--text_wrapper">
                                            <span
                                                >I am sorry but it seems you
                                                don't have the required
                                                permission to add new snippets
                                                to this topic.</span
                                            >
                                        </div>
                                    </div>
                                {:else if $ActiveTopic?.snippets?.length === 0 && $ExtensionStore.card.content === ""}
                                    <div class="empty-state">
                                        <img
                                            src={SnippetEmptyStateImg}
                                            alt="Empty snippet"
                                            class="empty-snippet__image"
                                        />
                                        <div class="empty-state--text_wrapper">
                                            <span
                                                ><span class="highlighted-text"
                                                    >Select any text</span
                                                >
                                                from this page and it will appear
                                                as a
                                                <strong>Snippet</strong> above.</span
                                            >
                                            <span
                                                >To save <strong>Snippet</strong
                                                >
                                                click the
                                                <strong>Create</strong>
                                                button at the bottom of that
                                                <strong>Snippet</strong>.</span
                                            >
                                        </div>
                                    </div>
                                {/if}
                            {/if}
                        </div>

                        {#if $SelectedSnippetsStore.length > 0}
                            <div class="popup-modal-wrapper">
                                <BottomPopUpModal
                                    actions={[deleteModalAction]}
                                    {initialText}
                                    confirmationTexts={[deleteConfirmationText]}
                                    isExtension={true}
                                />
                            </div>
                        {/if}

                        <footer>
                            {#if !$ExtensionStore.loading && $ActiveTopic && !noLocalSnippetsYet}
                                <ShareInput
                                    shareButtonClicked={() =>
                                        ExtensionStore.setPanel(
                                            ExtensionPanels.SHARE,
                                        )}
                                    topic={$ActiveTopic}
                                    showDashboardButton={true}
                                ></ShareInput>
                                {#if !subscriptionExpired && userRole >= TopicAccessLevel.EDITOR}
                                    <div class="ml-auto">
                                        <BookmarkButton />
                                    </div>
                                {/if}
                            {/if}
                        </footer>
                    {/if}
                {/if}
            </main>
        </div>
    {/if}
{/if}

<style>
    @import "./main.css";
    @import "./shadowdom-reset.css";

    /* purgecss start ignore */
    @import "./extension/external.css";

    @import "./extension/reset.css";
    /* purgecss end ignore */

    /** Setting up EM*/
    main,
    .arrow {
        font-size: 16px !important;
        cursor: default;
    }

    .app-wrapper {
        @apply fixed left-0 top-0 z-[2100000000] h-[100vh] w-full overflow-x-hidden overscroll-contain shadow-2xl transition-all sm:w-[350px] !important;
    }

    .app-wrapper.hide {
        @apply w-0 overflow-hidden !important;
    }

    .app-wrapper--panel-right {
        @apply left-auto right-0 !important;
    }

    main {
        @apply reset relative float-right flex h-full w-full flex-col bg-gray-100 selection:bg-pink-200 selection:text-black sm:w-[350px] !important;
    }

    .arrow {
        @apply reset fixed left-0 right-auto top-0 z-[2100000001] flex h-10 cursor-pointer select-none items-center justify-center rounded-r-full bg-gray-700 p-3 transition-all !important;
    }

    .arrow--panel-right {
        @apply left-0 right-auto rounded-l-full rounded-r-none transition-all sm:left-auto sm:right-[305px] !important;
    }

    .arrow--panel-right.arrow--panel-right--hidden {
        @apply left-auto right-0 transition-all !important;
    }

    .arrow svg {
        @apply reset h-5 w-5 select-none text-white transition-all !important;
    }

    .arrow svg path {
        @apply text-white !important;
    }

    .arrow--panel-right svg {
        @apply rotate-180 !important;
    }

    .arrow--panel-right svg path {
        @apply text-white !important;
    }

    .arrow svg.rotate {
        @apply rotate-180 !important;
    }

    .arrow--panel-right svg.rotate {
        @apply rotate-0 !important;
    }

    .cards {
        @apply reset relative flex w-full flex-1 flex-col gap-4 overflow-auto overflow-x-hidden px-4 !important;
    }

    .cards::-webkit-scrollbar {
        @apply h-3 w-3 bg-gray-100 !important;
    }

    .cards::-webkit-scrollbar-thumb {
        @apply border-0 border-l border-solid border-gray-100 bg-gray-200 hover:bg-gray-300 !important;
    }

    .--hide {
        @apply hidden !important;
    }

    .loading {
        @apply reset absolute left-0 top-0 z-50 flex h-full w-full flex-col items-center justify-center bg-gray-100 !important;
    }

    .loading .loading-gif {
        @apply reset w-60 !important;
    }

    .loading .error-img {
        @apply reset w-64 !important;
    }

    .loading__text {
        @apply reset mt-2 text-base !important;
    }

    /* START: Styling tutorial images - - - - - - - */

    .empty-state {
        @apply reset flex h-full w-full items-center justify-center text-white !important;
    }

    .empty-state {
        @apply reset h-full w-full px-4 !important;
    }

    .empty-state img {
        @apply reset mt-4 w-full !important;
    }

    .empty-state .empty-state--text_wrapper {
        @apply reset mt-16 flex min-h-min flex-col items-center !important;
    }

    .empty-state span {
        @apply reset mx-auto mt-4 w-64 text-center text-black !important;
    }

    .empty-state .highlighted-text {
        @apply reset bg-yellow-200 px-1 text-black !important;
    }

    .empty-state .empty-snippet__image {
        @apply reset w-full pb-3 pl-8 pr-3 pt-12 !important;
    }

    /* END: Styling tutorial images - - - - - - - */

    .popup-modal-wrapper {
        @apply reset fixed bottom-0 left-0 z-50 h-28 w-full overflow-y-hidden !important;
    }

    footer {
        @apply reset z-10 mt-auto flex w-full flex-row items-center gap-2 p-4 shadow-[0_-12px_20px_0px_rgba(243,244,245,1)] !important;
    }
</style>
